import { useQuery } from "@tanstack/react-query";
import { addDays, addMonths, endOfMonth, startOfDay, startOfMonth, subMonths } from "date-fns";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, queryString } from "../../api/client";
import type { EventRecord } from "../../api/types";
import { useVenueScope } from "../../app/venueScope";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllowsIn } from "../../auth/permissions";
import { formatDateTime, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  type Column,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  PageHeader,
  StatusPill,
} from "../../ui/components";
import { ListPageSkeleton } from "../../ui/skeletons";
import { EVENT_STATUSES } from "./eventLifecycle";

// The client's own programme, which is what this product is for. The sibling list at /bookings
// answers the other question, "who has hired the space", and the two are deliberately separate:
// an internal festival has no payer, no enquiry to qualify and nobody to invoice, and putting it
// in a table built around those columns is how the event side ended up invisible.

interface EventListResponse {
  items: EventRecord[];
  from: string;
  to: string;
}

const RANGE_OPTIONS = [
  { value: "upcoming", label: "Next 90 days" },
  { value: "month", label: "This month" },
  { value: "year", label: "Next 12 months" },
  { value: "past", label: "Past 12 months" },
] as const;

type RangeKey = (typeof RANGE_OPTIONS)[number]["value"];

const SPACE_OPTIONS = [
  { value: "", label: "Any space" },
  { value: "standalone", label: "We hold the space" },
  { value: "booking", label: "Under a booking" },
];

function rangeFor(key: RangeKey): { from: Date; to: Date } {
  const today = startOfDay(new Date());
  if (key === "month") return { from: startOfMonth(today), to: endOfMonth(today) };
  if (key === "year") return { from: today, to: addMonths(today, 12) };
  if (key === "past") return { from: subMonths(today, 12), to: today };
  return { from: today, to: addDays(today, 90) };
}

export function EventsPage() {
  const auth = useAuth();
  // Planning is asked of the events role alone. Somebody who only runs the lettings holds
  // events.manage nowhere that matters here, and offering them the button would end in a refusal.
  const membership = auth.session?.selectedMembership;
  const canManage = membershipAllowsIn(membership, "events", "events.manage");
  const { selectedVenueId: venueId } = useVenueScope();
  const [range, setRange] = useState<RangeKey>("upcoming");
  const [status, setStatus] = useState("");
  const [space, setSpace] = useState("");
  const [search, setSearch] = useState("");

  const bounds = useMemo(() => rangeFor(range), [range]);
  const query = queryString({
    from: bounds.from.toISOString(),
    to: bounds.to.toISOString(),
    status,
    space,
    venueId,
  });
  const events = useQuery({
    queryKey: ["events", "list", query],
    queryFn: () => api<EventListResponse>(`/api/v1/events${query}`),
    // The venue scope resolves asynchronously; an empty venue would briefly list every
    // venue's programme and then snap to one, which reads as a glitch.
    enabled: Boolean(venueId),
  });

  // Status and space are narrowed by the server so the count in the card header is honest. Free
  // text stays here: it spans four fields at once and needs to answer while somebody is typing.
  const items = useMemo(() => {
    const all = events.data?.items ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((item) =>
      [item.title, item.reference, item.ownerName, item.bookingReference]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [events.data, search]);

  const columns: Column<EventRecord>[] = [
    {
      label: "Event",
      className: "cell-wrap",
      sortValue: (item) => item.title.toLowerCase(),
      render: (item) => <Link to={`/events/${item.id}`}>{item.title}</Link>,
    },
    {
      label: "Reference",
      sortValue: (item) => item.reference,
      render: (item) => item.reference,
    },
    {
      label: "Space",
      className: "cell-wrap",
      sortValue: (item) => (item.spaceSource === "booking" ? item.bookingReference : ""),
      render: (item) =>
        item.spaceSource === "booking" && item.bookingId ? (
          <Link to={`/bookings/${item.bookingId}`}>{item.bookingReference}</Link>
        ) : (
          "Held by this event"
        ),
    },
    {
      label: "Starts",
      // Sorted on the instant rather than the rendered text, so "1 Apr" cannot order ahead of
      // "2 Mar".
      sortValue: (item) => Date.parse(item.start),
      render: (item) => formatDateTime(item.start, item.timezone),
    },
    {
      label: "Status",
      sortValue: (item) => item.status,
      render: (item) => <StatusPill value={item.status} />,
    },
    {
      label: "Lead",
      className: "cell-wrap",
      sortValue: (item) => item.ownerName.toLowerCase(),
      render: (item) => item.ownerName || "Not assigned",
    },
  ];

  return (
    <>
      <PageHeader
        title="Events"
        description="Everything this organization is running: what it is, when it is, and who is answerable for it."
        actions={
          canManage ? (
            <Link className="button primary" to="/events/new">
              Plan an event
            </Link>
          ) : null
        }
      />
      <Card>
        <div className="filters">
          <Field label="Search">
            <input
              type="search"
              value={search}
              placeholder="Event, reference, lead or booking"
              onChange={(event) => setSearch(event.target.value)}
            />
          </Field>
          <Field label="Date range">
            <select value={range} onChange={(event) => setRange(event.target.value as RangeKey)}>
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Event status">
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All statuses</option>
              {EVENT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {titleCase(value)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Space">
            <select value={space} onChange={(event) => setSpace(event.target.value)}>
              {SPACE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Card>
      {events.isPending ? (
        <ListPageSkeleton title="" description="" filterCount={0} columnCount={6} rowCount={8} />
      ) : null}
      {events.error ? <ErrorPanel error={events.error} /> : null}
      {events.data && items.length === 0 ? (
        <EmptyState
          title="No events match these filters"
          description="Widen the date range or clear a filter. Events outside the selected window are not loaded."
          action={
            canManage ? (
              <Link className="button primary" to="/events/new">
                Plan an event
              </Link>
            ) : null
          }
        />
      ) : null}
      {events.data && items.length > 0 ? (
        <Card>
          <CardHeader
            title="Event list"
            description={`${items.length} ${items.length === 1 ? "event" : "events"} in this range`}
          />
          <DataTable
            caption="Events matching the current filters"
            columns={columns}
            items={items}
            getKey={(item) => item.id}
            defaultSort={{ label: "Starts", direction: "ascending" }}
          />
        </Card>
      ) : null}
    </>
  );
}
